/* Copyright (c) 2020-present CloudIO Inc */

import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionDownloadChartImage';

const ActionDownloadChartImageManager: ActionManagerFn<
  'ActionDownloadChartImage'
> = (platform: Platform): ActionManager<'ActionDownloadChartImage'> => {
  return {
    ...baseActionManager(platform, 'ActionDownloadChartImage'),
    defaultValue,
    props: {
      ...baseActionProps,
      chartItemId: {
        label: 'Chart Item ID',
        type: 'ItemID',
        initialFilter: 'Chartjs',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'Chartjs' && item?.type !== 'ChartJSTreemap') {
            return `Item id '${value}' doesn't belong to a Chart`;
          }
          return '';
        },
      },
      fileNameExpression: {
        label: 'File Name',
        type: 'expression',
        returnType: async () => 'string',
      },
      imageType: {
        label: 'Image Type',
        type: 'select',
        options: toOptions(['png', 'jpeg']),
      },
    },
    replaceableWith: ['ActionDownloadChartData'],
    name: 'Download Chart Image',
    getDisplayText: (item) => item.props.chartItemId,
    icon: { iconName: 'download' },
    description: `**Overview**<br/>
The Download Chart Image action is a visualization utility designed to capture the current state of a chart component and export it as an image file. Unlike the data download action which extracts the raw numbers, this component generates a visual snapshot (raster image) of the graph exactly as it appears to the user.<br/><br/>
For this action to function correctly, the target chart must be actively rendered and visible on the page at the moment of execution. The component supports flexible output formats, allowing the user to choose between standard image types. It is designed to be interchangeable with the ActionDownloadChartData component, giving builders the option to provide either visual or raw data exports using similar workflows.

**Key Features**<br/>
* **Visual Export:** Captures the rendered chart as a static image file, preserving colors, legends, and current visual state.
* **Format Flexibility:** Includes an imageType property that allows the developer to specify the output format, supporting both 'png' and 'jpeg'.
* **Strict Component Validation:** Implements validateItemId logic to ensure the target identifier belongs to a valid visualization component (Chartjs or ChartJSTreemap).
* **Dynamic Naming:** Features a fileNameExpression property, enabling the generation of context-aware filenames (e.g., "Monthly_Revenue_Oct2025.png") at runtime.

**Properties**<br/>
* **Chart Item ID (chartItemId):** An ItemID property that selects the specific chart to capture.
  * *Constraint:* The item must be of type Chartjs or ChartJSTreemap.
  * *Requirement:* The component must be visible on the screen.
* **File Name (fileNameExpression):** An expression property returning a string that defines the name of the resulting file.
* **Image Type:** A select property defining the file format.
  * *Options:* png, jpeg.

**Use Cases**<br/>
* **Presentation Materials:** Allowing users to download charts as PNGs to easily embed them into external slide decks or documents.
* **Archival Snapshots:** Saving the exact visual state of a dashboard at a specific point in time for record-keeping.`,
  };
};

export default ActionDownloadChartImageManager;
